import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  CreditCard,
  Pause,
  Play,
  Plus,
  Repeat,
  Send,
  Zap,
} from 'lucide-react';
import {
  ArchivedToggle,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  ExportCsvButton,
  PageHeader,
  SegmentedControl,
  Skeleton,
  StatCard,
  StatusPill,
  toast,
  type DataTableColumn,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { csvFilename, csvMoney, downloadCsv } from '../../lib/csv';
import { formatDate, formatMoney } from '../../lib/format';
import { describeError } from './api';
import {
  useGenerateRecurring,
  useRecurringInvoices,
  useRunDueRecurring,
  useUpdateRecurring,
  type RecurringTemplate,
} from './recurring-api';
import { RecurringFormModal } from './recurring-form';
import { LastChargeLine, StripeLinkModal } from './stripe-link';
import { RECURRING_TAB, STRIP_TABS } from './state-tabs';

/** A rollup — a total across a set — prints whole dollars (CLAUDE.md). */
const ROLLUP = { whole: true } as const;

/**
 * Recurring invoice schedules — the Recurring tab of Jesse's sheet, and the
 * job Harvest does for him every month.
 *
 * The table is deliberately the sheet's four columns (client · subject · next
 * invoice · amount) and nothing more; the two extras a screen can offer that a
 * spreadsheet cannot are a "due" pill and a button that writes the draft.
 */
/**
 * What the last automatic send did — said only when there is something to
 * say: a send that could not go (red, with the invoice it left as a draft),
 * or the last one that did.
 */
function AutoSendLine({ template: t }: { template: RecurringTemplate }) {
  const last = t.lastAutoSend;
  if (!t.autoSend || !last) return null;
  const trouble = last.outcome === 'refused' || last.outcome === 'failed' || last.outcome === 'held';
  return (
    <p
      className={cn(
        'max-w-[14rem] whitespace-normal text-caption',
        trouble ? 'font-semibold text-status-late' : 'text-grey-500',
      )}
      title={last.message}
    >
      {trouble ? 'Not sent' : 'Sent'}
      {last.invoiceId && last.number ? (
        <>
          {' '}
          <Link
            to={`/invoices/${last.invoiceId}`}
            onClick={(e) => e.stopPropagation()}
            className="font-semibold text-accent-700 hover:underline"
          >
            #{last.number}
          </Link>
        </>
      ) : null}{' '}
      · {formatDate(last.at)}
      {trouble ? ` — ${last.message.replace(/^Not sent — #\S+ is waiting as a draft: /, '')}` : ''}
    </p>
  );
}

export function RecurringInvoicesPage() {
  const query = useRecurringInvoices();
  const runDue = useRunDueRecurring();
  const generate = useGenerateRecurring();
  const update = useUpdateRecurring();

  const navigate = useNavigate();

  const [showPaused, setShowPaused] = useState(false);
  const [editing, setEditing] = useState<RecurringTemplate | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [stripeOpen, setStripeOpen] = useState(false);

  const all = query.data?.templates ?? [];
  const pausedCount = all.filter((t) => !t.isActive).length;
  const rows = useMemo(
    () => (showPaused ? all : all.filter((t) => t.isActive)),
    [all, showPaused],
  );
  const summary = query.data?.summary;
  const today = query.data?.today ?? '';
  /**
   * The raw template is shown ONCE per distinct pattern, not once per row.
   * Twelve schedules share `%invoice_issue_month_name%`, so printing it in mono
   * under every one of them taught the syntax on row 1 and then charged for it
   * eleven more times. Every row still carries it in its tooltip, and the page
   * explains the variables in prose below the table.
   */
  const templateExemplars = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of rows) {
      if (t.subject !== t.subjectPreview && !seen.has(t.subject)) seen.set(t.subject, t.id);
    }
    return new Set(seen.values());
  }, [rows]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (template: RecurringTemplate) => {
    setEditing(template);
    setFormOpen(true);
  };

  const runAll = () => {
    runDue.mutate(undefined, {
      onSuccess: (result) => {
        if (result.generated.length === 0) {
          toast.info('Nothing is due yet — nothing was created');
          return;
        }
        toast.success(
          `${result.generated.length} draft invoice${result.generated.length === 1 ? '' : 's'} created — review them on Invoices`,
        );
        if (result.errors.length > 0) {
          toast.error(`${result.errors.length} schedule(s) failed: ${result.errors[0].message}`);
        }
      },
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : 'Could not generate the drafts'),
    });
  };

  const generateOne = (template: RecurringTemplate) => {
    generate.mutate(template.id, {
      onSuccess: (invoice) =>
        toast.success(`Draft #${invoice.number} created for ${invoice.clientName}`),
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : 'Could not generate the draft'),
    });
  };

  const togglePause = (template: RecurringTemplate) => {
    update.mutate(
      { id: template.id, isActive: !template.isActive },
      {
        onSuccess: () => toast.success(template.isActive ? 'Schedule paused' : 'Schedule resumed'),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Could not update the schedule'),
      },
    );
  };

  const exportCsv = () => {
    downloadCsv(
      csvFilename({ entity: 'recurring-invoices', view: showPaused ? 'all' : 'active' }),
      [
        ['Client', 'Invoice subject', 'Next invoice', 'Repeats', 'Amount', 'Status'],
        ...rows.map((t) => [
          t.client.name,
          t.subjectPreview,
          t.nextIssueDate,
          t.cadenceLabel,
          csvMoney(t.amount),
          t.isActive ? (t.stripe ? 'stripe' : t.isDue ? 'due' : 'active') : 'paused',
        ]),
      ],
    );
  };

  const columns = useMemo<Array<DataTableColumn<RecurringTemplate>>>(
    () => [
      {
        key: 'client',
        header: 'Client',
        sortable: true,
        sortValue: (t) => t.client.name,
        render: (t) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {/* The row's one action is "edit this schedule", so the name is a
                  button, not a link to the client: two destinations in one row
                  is how a click becomes a guess. It is focusable because
                  `onRowClick` alone is mouse-only. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(t);
                }}
                className="truncate rounded font-medium text-secondary hover:text-accent-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              >
                {t.client.name}
              </button>
              {!t.isActive && <StatusPill status="neutral" label="Paused" />}
              {t.stripe && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-grey-200 px-1.5 text-caption font-semibold text-grey-600"
                  title="Paid by a Stripe subscription"
                >
                  <CreditCard className="h-3 w-3" aria-hidden />
                  <span className="hidden sm:inline">Stripe</span>
                  <span className="sr-only sm:hidden">Paid by Stripe</span>
                </span>
              )}
              {t.autoSend && t.isActive && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent/30 bg-accent/5 px-1.5 text-caption font-semibold text-accent-700"
                  title={
                    t.stripe
                      ? 'Sends automatically: emailed on the invoice date, paid by the subscription'
                      : 'Sends automatically: emailed to the billing contacts at 9:00 AM on the invoice date'
                  }
                >
                  <Send className="h-3 w-3" aria-hidden />
                  <span className="hidden sm:inline">Auto</span>
                  <span className="sr-only sm:hidden">Sends automatically</span>
                </span>
              )}
            </div>
            {/* Below md the subject column is hidden, so the caption carries
                the subject instead — the money column has to stay on screen
                without a horizontal scroll to find it. */}
            <p className="truncate text-caption text-grey-500 md:hidden">{t.subjectPreview}</p>
            <p className="hidden truncate text-caption capitalize text-grey-500 md:block">
              {t.kind}
              {t.lines.length > 1
                ? ` · ${t.lines.length} agreements`
                : t.agreement
                  ? ` · ${t.agreement.name}`
                  : ''}
            </p>
          </div>
        ),
        // Keep the sum of column caps small enough that the MONEY column stays
        // on screen at 390px — an oversized cap here silently pushes it off
        // (see CLAUDE.md on min-content widths).
        className: 'max-w-[7.5rem] sm:max-w-[13rem]',
      },
      {
        key: 'subject',
        header: 'Invoice subject',
        sortable: true,
        sortValue: (t) => t.subjectPreview,
        render: (t) => (
          <div className="min-w-0">
            <p
              className="truncate text-secondary"
              title={t.subject !== t.subjectPreview ? t.subject : undefined}
            >
              {t.subjectPreview}
            </p>
            {/* The template underneath, so it is obvious the month is filled in
                for you rather than typed once and left to rot — said once per
                distinct pattern (see `templateExemplars`). */}
            {templateExemplars.has(t.id) && (
              <p className="truncate font-mono text-caption text-grey-400" title={t.subject}>
                {t.subject}
              </p>
            )}
          </div>
        ),
        className: 'hidden max-w-[20rem] md:table-cell',
      },
      {
        key: 'nextIssueDate',
        header: 'Next invoice',
        sortable: true,
        sortValue: (t) => t.nextIssueDate,
        render: (t) =>
          // A Stripe-paid schedule has no draft date to show: the charge
          // issues its invoice. What it CAN say is what the last charge did.
          t.stripe ? (
            <div className="max-w-[12rem] whitespace-normal">
              <span className="text-body-sm font-semibold text-secondary">Billed by Stripe</span>
              <LastChargeLine charge={t.stripe.lastCharge} className="line-clamp-2" />
            </div>
          ) : (
            <div className="whitespace-nowrap">
              <span className={cn('num', t.isDue && 'font-semibold text-status-late')}>
                {formatDate(t.nextIssueDate)}
              </span>
              <p className="text-caption text-grey-500">
                {t.isDue && <span className="font-semibold text-status-late">Due now · </span>}
                {t.cadenceLabel}
              </p>
              <AutoSendLine template={t} />
            </div>
          ),
        className: 'whitespace-nowrap',
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortable: true,
        sortValue: (t) => t.amount,
        // A document figure — somebody pays that exactly — so it keeps cents.
        render: (t) => <span className="num-cell">{formatMoney(t.amount)}</span>,
        className: 'whitespace-nowrap',
      },
      {
        key: 'actions',
        header: '',
        render: (t) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              title={`Create the ${formatDate(t.nextIssueDate)} draft now`}
              onClick={(e) => {
                e.stopPropagation();
                generateOne(t);
              }}
            >
              <Zap className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Generate</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              title={t.isActive ? 'Pause this schedule' : 'Resume this schedule'}
              aria-label={t.isActive ? 'Pause this schedule' : 'Resume this schedule'}
              onClick={(e) => {
                e.stopPropagation();
                togglePause(t);
              }}
            >
              {t.isActive ? (
                <Pause className="h-4 w-4" aria-hidden />
              ) : (
                <Play className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </div>
        ),
        align: 'right',
        // Below sm the row itself opens the editor, which carries the same two
        // actions — three columns and a button bar do not fit 390px, and a
        // clipped money column is the one thing this table may not do.
        className: 'hidden whitespace-nowrap sm:table-cell',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateExemplars],
  );

  if (query.isError) {
    const described = describeError(query.error, 'recurring schedules');
    return (
      <div>
        <BackLink />
        <PageHeader title="Recurring invoices" />
        <ErrorState
          card
          title={described.title}
          message={described.description}
          onRetry={() => query.refetch()}
        />
      </div>
    );
  }

  return (
    <div>
      {/* No BackLink here: the strip below is the way back, and two controls
          for one idea is how screens start disagreeing. Loading/error states
          keep theirs — they have no strip. */}
      <PageHeader
        title="Recurring invoices"
        subtitle={
          summary && summary.stripeLinked > 0
            ? 'Schedules that draft an invoice for you. Stripe-paid ones issue it when the charge lands and email the receipt.'
            : 'Schedules that draft an invoice for you. Nothing is ever sent automatically.'
        }
        actions={
          <>
            <ExportCsvButton
              count={rows.length}
              noun="schedules"
              size="md"
              onClick={exportCsv}
            />

            {summary && summary.dueCount > 0 && (
              <Button variant="accent" loading={runDue.isPending} onClick={runAll}>
                <Zap className="h-4 w-4" aria-hidden />
                Generate {summary.dueCount} due draft{summary.dueCount === 1 ? '' : 's'}
              </Button>
            )}
            <Button
              variant="outline"
              aria-label="Link Stripe subscriptions"
              onClick={() => setStripeOpen(true)}
            >
              <CreditCard className="h-4 w-4" aria-hidden />
              {/* Two buttons in the full wording do not fit 390px. */}
              <span className="sm:hidden">Link Stripe</span>
              <span className="hidden sm:inline">Link Stripe subscriptions</span>
            </Button>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" aria-hidden />
              New schedule
            </Button>
          </>
        }
      />

      {/* The same strip /invoices wears, with Recurring active: the state
          tabs navigate back to the list so the strip answers "where am I?"
          from either side. Its own row — inside the header's actions it ran
          off the right edge at 390px. */}
      <div className="mb-4 min-w-0">
        <SegmentedControl
          ariaLabel="Invoice state filter"
          size="md"
          scrollable
          value={RECURRING_TAB}
          onChange={(value) =>
            navigate(value === 'OPEN' ? '/invoices' : `/invoices?state=${value}`)
          }
          options={STRIP_TABS}
        />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {query.isLoading || !summary ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <StatCard
              kind="invoice"
              label="Active schedules"
              value={summary.active}
              hint={
                summary.inactive > 0
                  ? `${summary.inactive} paused`
                  : summary.active > 0
                    ? 'All running'
                    : 'None set up yet'
              }
            />
            <StatCard
              kind="revenue"
              label="Recurring revenue"
              value={`${formatMoney(summary.perMonth, ROLLUP)}/mo`}
              // A quarterly or annual schedule bills its WHOLE amount on its
              // date; only this tile averages it. Say so where the average is
              // printed, so nobody reads a $12k annual retainer as $1k a month
              // of cash that never arrives.
              hintLines={summary.nonMonthly > 0 ? 2 : 1}
              hint={
                summary.nonMonthly > 0 ? (
                  <span
                    title={`${summary.nonMonthly} schedule${summary.nonMonthly === 1 ? '' : 's'} bill quarterly or annually. This tile averages them; each one bills its whole amount on its own date, and the cash forecast puts it in that period.`}
                  >
                    {formatMoney(summary.next12Months, ROLLUP)} over the next 12 months ·{' '}
                    {summary.nonMonthly} not monthly, averaged here
                  </span>
                ) : (
                  `${formatMoney(summary.next12Months, ROLLUP)} scheduled over the next 12 months`
                )
              }
            />
            <StatCard
              kind="draft"
              label="Due now"
              value={summary.dueCount}
              hint={
                summary.dueCount > 0
                  ? `${formatMoney(summary.dueAmount, ROLLUP)} waiting to be drafted`
                  : 'Nothing to draft today'
              }
            />
          </>
        )}
      </div>

      {/* What this screen is for, said once. Two questions land people here:
          "will these bill themselves?" (no — they draft) and "where do the
          drafts go?" (Invoices). */}
      {/* Hidden with nothing to explain — the empty state below already says
          it, and two explanations of one idea is one too many. */}
      <Card
        className={cn(
          'mb-4 flex-wrap items-center gap-x-2 gap-y-1 border-accent/20 bg-accent-50/40 px-3 py-2 text-caption text-grey-700',
          all.length === 0 ? 'hidden' : 'flex',
        )}
      >
        <CalendarClock className="h-4 w-4 shrink-0 text-accent" aria-hidden />
        <span>
          A schedule creates a <strong className="font-semibold text-secondary">draft</strong> on its
          next invoice date — subject variables like{' '}
          <code className="font-mono">%invoice_issue_month_name%</code> filled in for that date.
        </span>
        <Link to="/invoices?state=DRAFT" className="font-semibold text-accent-700 hover:underline">
          Review drafts →
        </Link>
      </Card>

      {query.isLoading ? (
        <Skeleton className="h-64" />
      ) : all.length === 0 ? (
        <EmptyState
          icon={<Repeat aria-hidden />}
          title="No recurring schedules yet"
          description="Retainers, care plans and hosting bill the same amount every month. Set one up and the draft writes itself — you review and send."
          action={
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" aria-hidden />
              New schedule
            </Button>
          }
        />
      ) : (
        <Card className="p-0">
          <div className="flex items-center justify-between gap-3 border-b border-grey-200/70 px-3 py-2">
            <p className="text-caption text-grey-500">
              {rows.length} schedule{rows.length === 1 ? '' : 's'}
            </p>
            <ArchivedToggle
              active={showPaused}
              count={pausedCount}
              onChange={setShowPaused}
              noun="schedules"
            />
          </div>
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(t) => t.id}
            onRowClick={openEdit}
            rowLabel={(t) => `Edit the ${t.client.name} schedule`}
            defaultSort={{ key: 'nextIssueDate', dir: 'asc' }}
            density="compact"
            empty={
              <EmptyState
                title="Every schedule is paused"
                description="Turn one back on, or add a new schedule."
              />
            }
            footer={
              rows.length > 0 && summary ? (
                <tr>
                  <td className="px-3 py-2 text-caption font-semibold text-grey-600">
                    {summary.active} active schedule{summary.active === 1 ? '' : 's'}
                  </td>
                  {/* Mirrors the subject column's own responsive hiding, so the
                      footing stays under the figures it foots. */}
                  <td className="hidden md:table-cell" />
                  <td />
                  <td className="px-3 py-2 text-right">
                    <div className="num-cell font-bold text-secondary">
                      {formatMoney(summary.perMonth, ROLLUP)}
                    </div>
                    <div className="text-caption font-normal text-grey-500">a month</div>
                  </td>
                  <td className="hidden sm:table-cell" />
                </tr>
              ) : null
            }
          />
        </Card>
      )}

      <StripeLinkModal open={stripeOpen} onOpenChange={setStripeOpen} />

      <RecurringFormModal
        open={formOpen}
        template={editing}
        today={today}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/invoices"
      className="mb-2 inline-flex items-center gap-1 text-caption font-semibold text-grey-500 hover:text-accent-700"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      Invoices
    </Link>
  );
}
